import { forwardRef } from "react";

import { Box, BoxProps, useTheme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useInteractiveElementEvents } from "@/common/hooks/useInteractiveElementEvents";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

export type FormIconProps = BoxProps & {
  size?: "xsmall" | "small" | "medium" | "large" | "other";
  disabled?: boolean;
};

export const FormIcon = forwardRef<HTMLDivElement, FormIconProps>(
  ({ sx, children, size = "medium", flexShrink, disabled, onClick, onKeyDown, onKeyUp, ...props }, ref) => {
    const theme = useTheme();
    const { handleKeyDown, handleKeyUp } = useInteractiveElementEvents<HTMLDivElement>({ onClick, onKeyDown, onKeyUp });

    if (!theme.palette.forms || !theme.palette.formsComponents?.global) {
      return <InvalidThemeErrorMessage />;
    }

    const getSize = () => {
      switch (size) {
        case "xsmall":
          return "12px";
        case "small":
          return "16px";
        case "medium":
          return "20px";
        case "large":
          return "24px";
        default:
          return undefined;
      }
    };

    const styles = mergeSx(
      {
        fill: "currentColor",
        width: getSize(),
        height: getSize(),
        transition: "outline 100ms ease-out",
        lineHeight: 0,
        flexShrink: 0,
        outlineColor: "transparent",
        ["&:focus-visible"]: {
          borderRadius: "50%", // This is to make the outline round
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
        ...(disabled && {
          pointerEvents: "none",
          opacity: 0.4,
        }),
      },
      sx
    );

    const isButton = Boolean(onClick);

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <Box
        flexShrink={flexShrink ?? 0}
        role={isButton ? "button" : undefined}
        tabIndex={isButton ? (disabled ? -1 : 0) : undefined}
        aria-disabled={(isButton && disabled) || undefined}
        {...props}
        onClick={onClick}
        onKeyDown={onClick ? handleKeyDown : onKeyDown}
        onKeyUp={onClick ? handleKeyUp : onKeyUp}
        sx={styles}
        ref={ref}
      >
        {children}
      </Box>
    );
  }
);

FormIcon.displayName = "FormIcon";
